import React, { useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { UpdateProjectRequest, queryKeys } from 'generated/index';
import { useNavParams } from 'hooks';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import { mutations, queries } from 'RestAPIs/rapini';

import { Button } from '@mui/material';

import { Autocomplete } from 'components/shared';
import { EditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import BackToListButton from 'components/shared/CRUD/components/InnerPage/components/BackToListButton';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';

import EditProjectForm from './EditProjectForm';
import ParticipantsGrid from './ParticipantsGrid';
import { addEditProjectSchema } from './add-edit-project-schema';
import EditFooter from 'components/shared/CRUD/components/InnerPage/components/EditFooter';
import { StyledUnsavedDataAlertRepositioned } from 'components/shared/CRUD/components/InnerPage/EditPage.styles';
import UnsavedDataAlert from 'components/shared/CRUD/components/InnerPage/components/UnsavedDataAlert';
import {
  StyledAutocompleteBox,
  StyledEditProjectPageContainer,
  StyledEditProjectTopContent
} from './EditProjectPage.styles';
import {
  StyledBoxFlexCenter,
  StyledBoxFlexColumnSmallGap
} from 'components/shared/Box/Box.styles';

type Form = InferType<typeof addEditProjectSchema>;

type Option = {
  label: string;
  value: string;
};

type EditProjectPageProps = {
  projectEntityId: string;
};

export default function EditProjectPage({
  projectEntityId
}: EditProjectPageProps) {
  const { t } = useTranslation();
  const methods = useForm<Form>({
    resolver: yupResolver(addEditProjectSchema)
  });
  const { control, formState, handleSubmit, reset } = methods;
  const { fields, append, update, remove } = useFieldArray({
    name: 'participants',
    control
  });

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const { setParams } = useNavParams();

  function onSelected(option) {
    setParams({ projectId: option.value });
  }

  const { data: projects } = queries.useGetProjects(undefined, undefined);
  const project = projects?.find((p) => p.entityId === projectEntityId);

  useEffect(() => {
    if (project) {
      reset({
        name: project.name,
        description: project.description,
        validFrom: dayjs(project.validFrom),
        validTo: dayjs(project.validTo),
        participants:
          project?.participants?.map((p) => ({
            ...p,
            employee: {
              entityId: p.entityId,
              participantEntityId: p.participantEntityId,
              name: p.name
            }
          })) ?? []
      });
    }
  }, [project]);

  const { mutate: updateProject } = mutations.useUpdateProject();
  const queryClient = useQueryClient();

  async function onSubmit(data: Form) {
    const { name, description, validFrom, validTo, participants } = data;

    if (project) {
      const payload: UpdateProjectRequest = {
        entityId: project.entityId,
        name,
        description,
        validFrom: dayjs(validFrom).format('YYYY-MM-DD'),
        validTo: dayjs(validTo).format('YYYY-MM-DD'),
        participants:
          participants?.map((p) => ({
            entityId: p?.employee.entityId ?? null,
            participantEntityId: p.employee.participantEntityId,
            role: p.role,
            validFrom: dayjs(p.validFrom).format('YYYY-MM-DD'),
            validTo: dayjs(p.validTo).format('YYYY-MM-DD')
          })) ?? []
      };

      await updateProject(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getProjects.name);
          showSuccessSnackbar();
        },
        onError: () => {
          showErrorSnackbar();
        }
      });

      setIsEditMode(false);
      return;
    }
  }

  const options: Option[] =
    projects?.map((p) => ({
      label: p?.name ?? '-',
      value: p.entityId as string
    })) ?? [];

  const sortedOptions = options?.sort((a, b) => a.label.localeCompare(b.label));
  const defaultValue = useMemo(
    () => options.find(({ value }) => value === project?.entityId),
    [project]
  );

  function handleCancel() {
    setIsEditMode(false);
    reset();
  }

  return (
    <FormProvider {...methods}>
      <EditPageContext.Provider value={{ isEditable: isEditMode }}>
        <StyledEditProjectPageContainer>
          <StyledEditProjectTopContent>
            <StyledBoxFlexCenter>
              {defaultValue && (
                <StyledAutocompleteBox>
                  <Autocomplete
                    variant="outlined"
                    label="Project"
                    idProperty="value"
                    value={defaultValue}
                    options={sortedOptions}
                    onSelected={onSelected}
                    disableClearable
                    disabled={isEditMode}
                    fullWidth
                  />
                </StyledAutocompleteBox>
              )}
              <BackToListButton href="/#roster,administrator&page=PROJECTS" />
            </StyledBoxFlexCenter>
            {!isEditMode && project?.isEditable && (
              <Button
                variant="contained"
                color="primary"
                disabled={isEditMode}
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {t('edit')}
              </Button>
            )}
            {formState.isDirty && isEditMode && (
              <StyledUnsavedDataAlertRepositioned>
                <UnsavedDataAlert textContent={t('unsavedData')} />
              </StyledUnsavedDataAlertRepositioned>
            )}
          </StyledEditProjectTopContent>

          <form>
            <StyledBoxFlexColumnSmallGap>
              <EditProjectForm control={control} />
              <ParticipantsGrid
                isEditMode={isEditMode}
                participants={fields}
                append={append}
                update={update}
                remove={remove}
                projectValidFrom={project?.validFrom || ''}
                projectValidTo={project?.validTo || ''}
              />
            </StyledBoxFlexColumnSmallGap>

            {isEditMode && (
              <EditFooter
                onCancelClick={handleCancel}
                onClickSave={(e: React.MouseEvent) => {
                  e.preventDefault();
                  handleSubmit(onSubmit)();
                }}
                submitDisabled={!formState.isDirty}
              />
            )}
          </form>
        </StyledEditProjectPageContainer>
      </EditPageContext.Provider>
    </FormProvider>
  );
}
